import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Label,
  Tooltip
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface IronCondorProps {
  symbol: string;
  currentPrice?: number;
}

const IronCondor: React.FC<IronCondorProps> = ({ 
  symbol, 
  currentPrice = 100 // Default value if not provided
}) => {
  // Generate options data - in a real app this would come from an API
  const putLowerStrike = Math.round(currentPrice * 0.85); // 15% below current price
  const putUpperStrike = Math.round(currentPrice * 0.95); // 5% below current price
  const callLowerStrike = Math.round(currentPrice * 1.05); // 5% above current price
  const callUpperStrike = Math.round(currentPrice * 1.15); // 15% above current price
  
  const putLowerPremium = Math.round(currentPrice * 0.01); // 1% of current price
  const putUpperPremium = Math.round(currentPrice * 0.02); // 2% of current price
  const callLowerPremium = Math.round(currentPrice * 0.02); // 2% of current price
  const callUpperPremium = Math.round(currentPrice * 0.01); // 1% of current price
  
  const netCredit = putUpperPremium - putLowerPremium + callLowerPremium - callUpperPremium;
  const maxProfit = netCredit;
  const maxLoss = (putUpperStrike - putLowerStrike) - netCredit; // Same as (callUpperStrike - callLowerStrike) - netCredit

  const lowerBreakEven = putUpperStrike - netCredit;
  const upperBreakEven = callLowerStrike + netCredit;

  // Generate payoff diagram data
  const generatePayoffData = () => {
    const startPrice = currentPrice * 0.7; // Start at 70% of current price
    const endPrice = currentPrice * 1.3; // End at 130% of current price
    const step = (endPrice - startPrice) / 30;
    
    const data = [];
    for (let price = startPrice; price <= endPrice; price += step) {
      let payoff;
      
      if (price <= putLowerStrike) {
        // Below put lower strike - max loss on put side
        payoff = -maxLoss;
      } else if (price > putLowerStrike && price < putUpperStrike) {
        // Between put strikes - variable loss
        payoff = price - putLowerStrike - maxLoss;
      } else if (price >= putUpperStrike && price <= callLowerStrike) {
        // Between put upper strike and call lower strike - max profit zone
        payoff = maxProfit;
      } else if (price > callLowerStrike && price < callUpperStrike) {
        // Between call strikes - variable loss
        payoff = callUpperStrike - price - maxLoss;
      } else {
        // Above call upper strike - max loss on call side
        payoff = -maxLoss;
      }
      
      data.push({ price: Math.round(price), payoff: Math.round(payoff * 100) / 100 });
    }
    
    return data;
  };

  const payoffData = generatePayoffData();

  return (
    <Card className="shadow-sm border-t-4 border-t-purple-500">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-lg">
            Iron Condor for {symbol}
            <Badge variant="secondary" className="ml-2">Neutral Strategy</Badge>
          </CardTitle>
        </div>
        <CardDescription>
          An iron condor is created by selling an out-of-the-money put spread and an out-of-the-money call spread.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-base font-medium mb-2">Strategy Overview</h3>
            <p className="text-sm text-gray-700 mb-4">
              This strategy is used when you expect {symbol} to stay within a specific price range. It profits from low volatility and time decay.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700">Put Spread</h4>
                <div className="space-y-1">
                  <p className="text-xs">Buy Put: <span className="font-semibold">${putLowerStrike}</span></p>
                  <p className="text-xs text-gray-500">Premium: ${putLowerPremium}</p>
                  <p className="text-xs mt-2">Sell Put: <span className="font-semibold">${putUpperStrike}</span></p>
                  <p className="text-xs text-gray-500">Premium: ${putUpperPremium}</p>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700">Call Spread</h4>
                <div className="space-y-1">
                  <p className="text-xs">Sell Call: <span className="font-semibold">${callLowerStrike}</span></p>
                  <p className="text-xs text-gray-500">Premium: ${callLowerPremium}</p>
                  <p className="text-xs mt-2">Buy Call: <span className="font-semibold">${callUpperStrike}</span></p>
                  <p className="text-xs text-gray-500">Premium: ${callUpperPremium}</p>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700">Max Profit</h4>
                <p className="text-xl font-bold text-green-600">${maxProfit}</p>
                <p className="text-xs text-gray-500">When price stays between ${putUpperStrike} and ${callLowerStrike}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700">Max Loss</h4>
                <p className="text-xl font-bold text-red-600">${maxLoss}</p>
                <p className="text-xs text-gray-500">Limited to width of spread - net credit</p>
              </div>
            </div>
            
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="bg-blue-50 p-3 rounded-lg">
                <h4 className="text-sm font-medium text-blue-700">Lower Break-even</h4>
                <p className="text-lg font-bold">${lowerBreakEven}</p>
                <p className="text-xs text-gray-600">Put upper strike - net credit</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <h4 className="text-sm font-medium text-blue-700">Upper Break-even</h4>
                <p className="text-lg font-bold">${upperBreakEven}</p>
                <p className="text-xs text-gray-600">Call lower strike + net credit</p>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-base font-medium mb-2">Payoff Diagram</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                data={payoffData}
                margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="price" 
                  domain={['auto', 'auto']}
                  tick={{ fontSize: 12 }}
                >
                  <Label value="Stock Price" position="bottom" offset={0} />
                </XAxis>
                <YAxis
                  domain={[Math.min(-maxLoss*1.2, -10), Math.max(maxProfit*1.2, 10)]}
                  tick={{ fontSize: 12 }}
                >
                  <Label value="Profit/Loss" position="left" angle={-90} offset={-5} style={{ textAnchor: 'middle' }} />
                </YAxis>
                <Tooltip formatter={(value) => [`$${value}`, 'Profit/Loss']} labelFormatter={(label) => `Price: $${label}`} />
                
                {/* Zero reference line */}
                <ReferenceLine y={0} stroke="#9ca3af" strokeWidth={1} />
                
                {/* Strike prices reference lines */}
                <ReferenceLine x={putLowerStrike} stroke="#9333ea" strokeDasharray="3 3">
                  <Label value={`Put Lower: $${putLowerStrike}`} position="top" fill="#9333ea" fontSize={10} />
                </ReferenceLine>
                <ReferenceLine x={putUpperStrike} stroke="#a855f7" strokeDasharray="3 3">
                  <Label value={`Put Upper: $${putUpperStrike}`} position="top" fill="#a855f7" fontSize={10} />
                </ReferenceLine>
                <ReferenceLine x={callLowerStrike} stroke="#a855f7" strokeDasharray="3 3">
                  <Label value={`Call Lower: $${callLowerStrike}`} position="top" fill="#a855f7" fontSize={10} />
                </ReferenceLine>
                <ReferenceLine x={callUpperStrike} stroke="#9333ea" strokeDasharray="3 3">
                  <Label value={`Call Upper: $${callUpperStrike}`} position="top" fill="#9333ea" fontSize={10} />
                </ReferenceLine>
                
                {/* Current price reference line */}
                <ReferenceLine x={currentPrice} stroke="#10b981" strokeWidth={2}>
                  <Label value={`Current: $${currentPrice}`} position="bottom" fill="#10b981" fontSize={11} />
                </ReferenceLine>
                
                {/* Break-even reference lines */}
                <ReferenceLine x={lowerBreakEven} stroke="#3b82f6" strokeDasharray="5 5">
                  <Label value={`Lower B/E: $${lowerBreakEven}`} position="insideBottomLeft" fill="#3b82f6" fontSize={10} />
                </ReferenceLine>
                <ReferenceLine x={upperBreakEven} stroke="#3b82f6" strokeDasharray="5 5">
                  <Label value={`Upper B/E: $${upperBreakEven}`} position="insideBottomRight" fill="#3b82f6" fontSize={10} />
                </ReferenceLine>
                
                {/* Profit/loss line */}
                <Line
                  type="monotone"
                  dataKey="payoff"
                  stroke="#8b5cf6"
                  strokeWidth={3}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
            
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
              <h4 className="text-sm font-medium text-yellow-800">How It Works</h4>
              <ul className="text-xs text-gray-700 space-y-1 mt-1 list-disc pl-4">
                <li>Buy 1 put option at ${putLowerStrike} strike</li>
                <li>Sell 1 put option at ${putUpperStrike} strike</li>
                <li>Sell 1 call option at ${callLowerStrike} strike</li>
                <li>Buy 1 call option at ${callUpperStrike} strike</li>
                <li>All options have the same expiration date</li>
                <li>Maximum profit is achieved when ${symbol} remains between ${putUpperStrike} and ${callLowerStrike} at expiration</li>
                <li>The strategy benefits from time decay and decreased volatility</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default IronCondor;